import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { DollarSign, Receipt, Save, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { usePageHeader } from "../../contexts/PageHeaderContext";
import { useScreenPermission } from "../../hooks/useScreenPermission";
import { FullScreenLoading } from "../../components/ui/loader";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { InputFieldset } from "../../ui/inputFieldset";
import { SelectFieldset } from "../../ui/selectFieldset";
import { SelectTriggerUI } from "../../ui/selectTrigger";
import { TextareaUI } from "../../ui/textareaUI";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from "../../ui/select";
import {
  getApprovedTourOperators,
  getBaseCurrency,
} from "../../services/advanceReceiptService";
import type {
  BaseCurrencyApi,
  TourOperatorApi,
} from "../../services/advanceReceiptService";
import {
  createBookingReceipt,
  getBookingReceiptById,
  getToursByOperator,
  postBookingReceipt,
  updateBookingReceipt,
} from "../../services/bookingReceiptService";

interface BookingReceiptTourOption {
  id: string;
  label: string;
  publicId?: string;
  lawazimAmount: number;
  balanceAmount: number;
}

const localDateValue = (date = new Date()) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(date.getDate()).padStart(2, "0")}`;

function isValidReceivedDate(value: string, today = localDateValue()): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }

  const parsed = new Date(`${value}T00:00:00`);

  return (
    Number.isFinite(parsed.getTime()) &&
    localDateValue(parsed) === value &&
    value <= today
  );
}

interface BookingReceiptFormPageProps {
  onNavigate: (page: string) => void;
}

type FormErrors = Partial<
  Record<
    | "tourOperator"
    | "description"
    | "amount"
    | "lawazimAmount"
    | "tourId"
    | "receivedDate",
    string
  >
>;

const paymentModes = [
  "Cash",
  "Cheque",
  "Bank Transfer",
  "UPI",
  "Credit/Debit Card",
  "Refund",
];

export function BookingReceiptFormPage({
  onNavigate,
}: BookingReceiptFormPageProps) {
  const { user } = useAuth();
  const { canAdd, canEdit } = useScreenPermission();
  const { id: editingId } = useParams<{ id: string }>();
  const [receiptNo, setReceiptNo] = useState("Auto-generated");
  const { setPageHeader } = usePageHeader();
  const [searchParams] = useSearchParams();
  const pageFromList = searchParams.get("page");
  const listPath = pageFromList
    ? `/booking-receipts?page=${encodeURIComponent(pageFromList)}`
    : "/booking-receipts";
  const mayCreate =
    !!user && (user.isSuperAdmin || (editingId ? canEdit : canAdd));
  const [receiptDate, setReceiptDate] = useState(() => localDateValue());
  const [receivedDate, setReceivedDate] = useState(() => localDateValue());
  const [receivedBy, setReceivedBy] = useState("");
  const [tourId, setTourId] = useState("");
  const [tours, setTours] = useState<BookingReceiptTourOption[]>([]);
  const [lookupsLoading, setLookupsLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");

  const [tourOperatorId, setTourOperatorId] = useState("");
  const [tourOperators, setTourOperators] = useState<TourOperatorApi[]>([]);
  const [tourOperatorSearch, setTourOperatorSearch] = useState("");
  const [tourSearch, setTourSearch] = useState("");
  const [editTourCode, setEditTourCode] = useState("");

  const [, setBaseCurrency] = useState<BaseCurrencyApi | null>(null);

  const [paymentMode, setPaymentMode] = useState("Cash");
  const [transactionRef, setTransactionRef] = useState("");
  const [remarks, setRemarks] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [lawazim, setLawazim] = useState<{
    tourId: string;
    amount: number | null;
    error: string;
  }>({
    tourId: "",
    amount: null,
    error: "",
  });
  const [balanceAmount, setBalanceAmount] = useState<number | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const saving = useRef(false);

  const getTourOperatorData = async () => {
    try {
      setLoading(true);
      const res = await getApprovedTourOperators();
      setTourOperators(res);
    } catch {
      toast.error("Failed to load Tour Operators");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTourOperatorData();
  }, []);

  useEffect(() => {
    if (!editingId) {
      return;
    }

    const loadBookingReceipt = async () => {
      try {
        setLoading(true);

        const response = await getBookingReceiptById(editingId);

        if (!response.success || !response.data) {
          throw new Error(
            response.message || "Failed to load booking receipt.",
          );
        }

        const receipt = response.data;

        setReceiptNo(receipt.receiptNumber);
        setReceiptDate(receipt.receiptDate.split("T")[0]);

        setTourOperatorId(String(receipt.tourOperatorId));
        setEditTourCode(receipt.tourCode);

        setReceivedBy(receipt.receivedBy);
        setReceivedDate(receipt.receivedDate.split("T")[0]);

        setPaymentMode(receipt.paymentMode);
        setTransactionRef(receipt.transactionReference ?? "");
        setRemarks(receipt.remarks ?? "");
        setDescription(receipt.description ?? "");
        setAmount(String(receipt.amountReceived));
      } catch (error) {
        console.error("Failed to load booking receipt:", error);

        const message =
          error instanceof Error
            ? error.message
            : "Failed to load booking receipt.";

        toast.error(message);
      } finally {
        setLoading(false);
      }
    };

    loadBookingReceipt();
  }, [editingId]);

  useEffect(() => {
    const loadBaseCurrency = async () => {
      try {
        setLoading(true);

        const res = await getBaseCurrency();

        setBaseCurrency(res);
      } catch (error) {
        console.error("Failed to load base currency:", error);
        toast.error("Failed to load base currency");
      } finally {
        setLoading(false);
      }
    };

    loadBaseCurrency();
  }, []);

  useEffect(() => {
    if (!tourOperatorId) {
      setTours([]);
      setTourId("");
      return;
    }

    const loadToursByOperator = async () => {
      try {
        setLookupsLoading(true);
        setLookupError("");
        setTourId("");

        const response = await getToursByOperator(Number(tourOperatorId));

        const mappedTours: BookingReceiptTourOption[] = response.map(
          (tour) => ({
            id: tour.publicId,
            label: tour.tourCode,
            publicId: tour.publicId,
            lawazimAmount: tour.lawazimAmount,
            balanceAmount: tour.balanceAmount,
          }),
        );

        setTours(mappedTours);
      } catch (error) {
        console.error("Failed to load tours:", error);

        setTours([]);
        setTourId("");
        setLookupError("Failed to load Tour Codes.");
        toast.error("Failed to load Tour Codes.");
      } finally {
        setLookupsLoading(false);
      }
    };

    loadToursByOperator();
  }, [tourOperatorId]);

  useEffect(() => {
    if (!editingId || !editTourCode || tours.length === 0) {
      return;
    }

    const matchingTour = tours.find((tour) => tour.label === editTourCode);

    if (matchingTour) {
      setTourId(matchingTour.id);
    }
  }, [editingId, editTourCode, tours]);

  const availableTours = tours;

  const filteredTours = availableTours.filter((tour) =>
    tour.label.toLowerCase().includes(tourSearch.trim().toLowerCase()),
  );

  const selectedTour = availableTours.find((tour) => tour.id === tourId);

  useEffect(() => {
    if (!selectedTour) {
      setLawazim({
        tourId: "",
        amount: null,
        error: "",
      });

      setBalanceAmount(null);

      setErrors((previous) => ({
        ...previous,
        lawazimAmount: undefined,
      }));

      return;
    }

    setLawazim({
      tourId: selectedTour.id,
      amount: selectedTour.lawazimAmount,
      error: "",
    });

    setBalanceAmount(selectedTour.balanceAmount);

    setErrors((previous) => ({
      ...previous,
      lawazimAmount: undefined,
    }));
  }, [selectedTour]);

  const lawazimAmount =
    selectedTour && lawazim.tourId === selectedTour.id ? lawazim.amount : null;

  const selectedBalanceAmount =
    selectedTour && lawazim.tourId === selectedTour.id ? balanceAmount : null;

  const lawazimError =
    selectedTour && lawazim.tourId === selectedTour.id ? lawazim.error : "";

  const lawazimLoading =
    !!selectedTour && lawazimAmount === null && !lawazimError;

  const filteredTourOperators = tourOperators
    .filter((operator) =>
      `${operator.firstName} ${
        operator.middleName ?? ""
      } ${operator.lastName} ${operator.itsNumber} ${operator.officeName}`
        .toLowerCase()
        .includes(tourOperatorSearch.trim().toLowerCase()),
    )
    .sort((a, b) =>
      a.officeName.localeCompare(b.officeName, undefined, {
        sensitivity: "base",
      }),
    );

  const selectedTourOperator = tourOperators.find(
    (operator) => String(operator.tourOperatorId) === tourOperatorId,
  );

  const totalAmount = Number(amount);

  const receivedCents = Math.round(
    (Number.isFinite(totalAmount) ? totalAmount : 0) * 100,
  );

  const summaryItems = [
    {
      label: "Total Lawazim Amount",
      cents: Math.round((lawazimAmount ?? 0) * 100),
    },
    {
      label: "Amount Received",
      cents: receivedCents,
    },
    {
      label: "Balance",
      cents: Math.round((selectedBalanceAmount ?? 0) * 100),
    },
  ];

  useEffect(() => {
    setPageHeader({
      title: editingId ? "Edit Booking Receipt" : "Create Booking Receipt",
      icon: <Receipt className="h-6 w-6" />,
      onBack: () => onNavigate(listPath),
    });

    return () => setPageHeader(null);
  }, [editingId, listPath, onNavigate, setPageHeader]);

  const handleSubmit = async (status: "DRAFT" | "POSTED" | "UPDATE") => {
    if (!mayCreate || !user) {
      toast.error(
        editingId
          ? "You do not have permission to edit receipts"
          : "You do not have permission to create receipts",
      );
      return;
    }

    if (saving.current || lookupsLoading || lookupError) {
      return;
    }

    const nextErrors: FormErrors = {};

    if (lawazimAmount === null) {
      nextErrors.lawazimAmount =
        "Select a tour and wait for its Lawazim amount to load";
    }

    if (!selectedTour) {
      nextErrors.tourId = "Tour Code is required";
    }

    if (!isValidReceivedDate(receivedDate)) {
      nextErrors.receivedDate = "Select today's date or a previous date";
    }

    if (!selectedTourOperator) {
      nextErrors.tourOperator = "Tour Operator is required";
    }

    if (!description.trim()) {
      nextErrors.description = "Description is required";
    } else if (!/^[A-Za-z0-9 ]{1,50}$/.test(description.trim())) {
      nextErrors.description =
        "Use only letters, numbers and spaces (maximum 50 characters).";
    }

    if (!Number.isFinite(totalAmount) || totalAmount <= 0) {
      nextErrors.amount = "Amount must be greater than 0";
    } else if (!/^\d+(\.\d{1,2})?$/.test(amount)) {
      nextErrors.amount = "Enter an amount with at most two decimal places";
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length) {
      return;
    }

    if (!selectedTourOperator || !selectedTour) {
      return;
    }

    try {
      saving.current = true;
      setLoading(true);

      const createPayload = {
        receiptDate,
        tourOperatorId: selectedTourOperator.tourOperatorId,
        tourCode: selectedTour.label,
        receivedBy: receivedBy || user.name,
        receivedDate,
        paymentMode,
        transactionReference: transactionRef,
        remarks,
        description: description.trim(),
        amountReceived: Math.round(totalAmount * 100) / 100,
      };

      if (editingId) {
        // EDIT MODE
        const response = await updateBookingReceipt(editingId, createPayload);

        toast.success(
          response.message || "Receipt draft updated successfully.",
        );
      } else {
        // CREATE MODE
        const response = await createBookingReceipt(createPayload);

        if (status === "POSTED") {
          const publicId = response.data?.publicId;

          if (!publicId) {
            throw new Error(
              "Receipt was created but publicId was not returned.",
            );
          }

          const postResponse = await postBookingReceipt(publicId);

          toast.success(postResponse.message || "Receipt posted successfully.");
        } else {
          toast.success(response.message || "Booking receipt saved as draft.");
        }
      }

      onNavigate(listPath);
    } catch (error) {
      console.error("Failed to create booking receipt:", error);

      const message = axios.isAxiosError(error)
        ? error.response?.data?.message ||
          error.response?.data?.errors?.[0] ||
          "Failed to create booking receipt."
        : error instanceof Error
          ? error.message
          : "Failed to create booking receipt.";

      toast.error(message);
    } finally {
      saving.current = false;
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <FullScreenLoading />}
      <form className="space-y-4 sm:space-y-6" noValidate>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            <Card
              className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-lg"
              style={{
                gap: "0px",
              }}
            >
              <CardHeader className="border-b border-gray-100 pb-4">
                <CardTitle
                  style={{
                    color: "#5B9BD5",
                  }}
                  className="flex items-center gap-2"
                >
                  <Receipt className="w-5 h-5" />
                  Receipt Details
                </CardTitle>
              </CardHeader>

              <CardContent className="p-4 sm:p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputFieldset
                    label="Receipt Number"
                    aria-label="Receipt Number"
                    value={receiptNo}
                    disabled
                    className="bg-gray-100 border-gray-300 mt-1.5 cursor-not-allowed"
                  />

                  <InputFieldset
                    label="Receipt Date"
                    aria-label="Receipt Date"
                    type="date"
                    value={receiptDate}
                    disabled
                    className="bg-gray-100 border-gray-300 mt-1.5 cursor-not-allowed"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectFieldset
                    label="Tour Operator"
                    required
                    error={errors.tourOperator}
                  >
                    <Select
                      value={tourOperatorId}
                      disabled={!!editingId || loading || !mayCreate}
                      onOpenChange={() => setTourOperatorSearch("")}
                      onValueChange={(value) => {
                        setTourOperatorId(value);

                        setErrors((previous) => ({
                          ...previous,
                          tourOperator: undefined,
                        }));
                      }}
                    >
                      <SelectTriggerUI
                        aria-label="Tour Operator"
                        aria-invalid={!!errors.tourOperator}
                        className="border-gray-300 mt-1.5"
                      >
                        <SelectValue placeholder="Select Tour Operator">
                          {selectedTourOperator?.officeName ||
                            "Select Tour Operator"}
                        </SelectValue>
                      </SelectTriggerUI>

                      <SelectContent>
                        <div className="px-2 pb-2 sticky top-0 bg-white z-10">
                          <div className="relative">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />

                            <Input
                              aria-label="Search tour operators"
                              placeholder="Search by name, ITS, or office..."
                              value={tourOperatorSearch}
                              onChange={(event) =>
                                setTourOperatorSearch(event.target.value)
                              }
                              onKeyDown={(event) => {
                                if (
                                  event.key !== "Escape" &&
                                  event.key !== "Tab"
                                ) {
                                  event.stopPropagation();
                                }
                              }}
                              onClick={(event) => event.stopPropagation()}
                              className="w-full pl-9 pr-3 py-2 border-gray-300 rounded-md"
                            />
                          </div>
                        </div>

                        <div className="max-h-60 overflow-y-auto">
                          {filteredTourOperators.length ? (
                            filteredTourOperators.map((operator) => (
                              <SelectItem
                                key={operator.tourOperatorId}
                                value={String(operator.tourOperatorId)}
                              >
                                <div className="flex flex-col py-1">
                                  <span className="font-medium">
                                    {operator.officeName}
                                  </span>
                                </div>
                              </SelectItem>
                            ))
                          ) : (
                            <div className="px-2 py-6 text-center text-sm text-gray-500">
                              No tour operators found
                            </div>
                          )}
                        </div>
                      </SelectContent>
                    </Select>
                  </SelectFieldset>

                  <SelectFieldset
                    label="Tour Code"
                    required
                    error={errors.tourId}
                  >
                    <Select
                      value={tourId}
                      disabled={
                        !!editingId ||
                        !tourOperatorId ||
                        lookupsLoading ||
                        loading ||
                        !mayCreate
                      }
                      onOpenChange={() => setTourSearch("")}
                      onValueChange={(value) => {
                        setTourId(value);

                        setErrors((previous) => ({
                          ...previous,
                          tourId: undefined,
                        }));
                      }}
                    >
                      <SelectTriggerUI
                        aria-label="Tour Code"
                        aria-invalid={!!errors.tourId}
                        className="border-gray-300 mt-1.5"
                      >
                        <SelectValue
                          placeholder={
                            tourOperatorId
                              ? "Select Tour Code"
                              : "Select Tour Operator first"
                          }
                        >
                          {availableTours.find((tour) => tour.id === tourId)
                            ?.label ||
                            (tourOperatorId
                              ? "Select Tour Code"
                              : "Select Tour Operator first")}
                        </SelectValue>
                      </SelectTriggerUI>

                      <SelectContent>
                        <div className="px-2 pb-2 sticky top-0 bg-white z-10">
                          <div className="relative">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />

                            <Input
                              aria-label="Search tour code"
                              placeholder="Search Tour Code..."
                              value={tourSearch}
                              onChange={(event) =>
                                setTourSearch(event.target.value)
                              }
                              onKeyDown={(event) => {
                                if (
                                  event.key !== "Escape" &&
                                  event.key !== "Tab"
                                ) {
                                  event.stopPropagation();
                                }
                              }}
                              onClick={(event) => event.stopPropagation()}
                              className="w-full pl-9 pr-3 py-2 border-gray-300 rounded-md"
                            />
                          </div>
                        </div>

                        <div className="max-h-60 overflow-y-auto">
                          {filteredTours.length ? (
                            filteredTours.map((tour) => (
                              <SelectItem key={tour.id} value={tour.id}>
                                <div className="flex flex-col py-1">
                                  <span className="font-medium">
                                    {tour.label}
                                  </span>
                                </div>
                              </SelectItem>
                            ))
                          ) : (
                            <div className="px-2 py-6 text-center text-sm text-gray-500">
                              No tour codes found
                            </div>
                          )}
                        </div>
                      </SelectContent>
                    </Select>
                  </SelectFieldset>
                </div>

                {lookupError && (
                  <div
                    role="alert"
                    className="flex items-center gap-3 text-sm text-red-600"
                  >
                    {lookupError}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputFieldset
                    label="Received By"
                    aria-label="Received By"
                    value={receivedBy || user?.name || ""}
                    readOnly
                    className="bg-gray-100 border-gray-300"
                  />

                  <div>
                    <InputFieldset
                      label="Received Date"
                      aria-label="Received Date"
                      type="date"
                      required
                      value={receivedDate}
                      max={localDateValue()}
                      disabled={loading || !mayCreate}
                      aria-invalid={!!errors.receivedDate}
                      aria-describedby={
                        errors.receivedDate ? "received-date-error" : undefined
                      }
                      onChange={(event) => {
                        setReceivedDate(event.target.value);

                        setErrors((previous) => ({
                          ...previous,
                          receivedDate: undefined,
                        }));
                      }}
                      className="border-gray-300"
                    />

                    {errors.receivedDate && (
                      <p
                        id="received-date-error"
                        className="text-red-500 text-xs mt-1"
                      >
                        {errors.receivedDate}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectFieldset label="Payment Mode" required>
                    <Select
                      value={paymentMode}
                      onValueChange={setPaymentMode}
                      disabled={!mayCreate}
                    >
                      <SelectTriggerUI
                        aria-label="Payment Mode"
                        className="border-gray-300"
                      >
                        <SelectValue placeholder="Select Payment Mode" />
                      </SelectTriggerUI>

                      <SelectContent>
                        {paymentModes.map((mode) => (
                          <SelectItem key={mode} value={mode}>
                            {mode}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </SelectFieldset>

                  <InputFieldset
                    label="Transaction Reference"
                    aria-label="Transaction Reference"
                    value={transactionRef}
                    disabled={!mayCreate}
                    onChange={(event) =>
                      setTransactionRef(
                        event.target.value
                          .toUpperCase()
                          .replace(/[^A-Z0-9 ]/g, ""),
                      )
                    }
                    placeholder="Cheque No / Transaction ID"
                    className="border-gray-300"
                  />
                </div>

                <SelectFieldset label="Remarks">
                  <TextareaUI
                    aria-label="Remarks"
                    value={remarks}
                    disabled={!mayCreate}
                    onChange={(event) =>
                      setRemarks(event.target.value.replace(/[^A-Za-z ]/g, ""))
                    }
                    placeholder="Additional notes or comments"
                    className="border-gray-300 mt-1.5 min-h-[80px]"
                  />
                </SelectFieldset>
              </CardContent>
            </Card>

            <Card
              className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-lg"
              style={{
                gap: "0px",
                marginTop: "10px",
              }}
            >
              <CardHeader className="border-b border-gray-100 pb-4">
                <CardTitle
                  style={{
                    color: "#5B9BD5",
                  }}
                  className="flex items-center gap-2"
                >
                  <DollarSign className="w-5 h-5" />
                  Amount Entries
                </CardTitle>
              </CardHeader>

              <CardContent className="p-4 sm:p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <InputFieldset
                      label="Description"
                      aria-label="Description"
                      required
                      value={description}
                      disabled={!mayCreate}
                      maxLength={50}
                      aria-invalid={!!errors.description}
                      aria-describedby={
                        errors.description
                          ? "booking-description-error"
                          : undefined
                      }
                      onChange={(event) => {
                        setDescription(
                          event.target.value
                            .replace(/[^A-Za-z0-9 ]/g, "")
                            .slice(0, 50),
                        );

                        setErrors((previous) => ({
                          ...previous,
                          description: undefined,
                        }));
                      }}
                      placeholder="e.g., Booking payment"
                      className="border-gray-300 mt-1"
                    />

                    {errors.description && (
                      <p
                        id="booking-description-error"
                        className="text-red-500 text-xs mt-1"
                      >
                        {errors.description}
                      </p>
                    )}
                  </div>

                  <div>
                    <InputFieldset
                      label="Amount Received (USD)"
                      aria-label="Amount Received (USD)"
                      required
                      type="number"
                      value={amount}
                      disabled={!mayCreate}
                      onChange={(event) => {
                        const value = event.target.value;

                        if (value && !/^\d*(\.\d{0,2})?$/.test(value)) {
                          return;
                        }

                        setAmount(value);

                        setErrors((previous) => ({
                          ...previous,
                          amount: undefined,
                        }));
                      }}
                      placeholder="0.00"
                      min="0.01"
                      step="0.01"
                      aria-invalid={!!errors.amount}
                      aria-describedby={
                        errors.amount ? "booking-amount-error" : undefined
                      }
                      className="border-gray-300 mt-1"
                    />

                    {errors.amount && (
                      <p
                        id="booking-amount-error"
                        className="text-red-500 text-xs mt-1"
                      >
                        {errors.amount}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-6 space-y-4">
              <Card
                className="bg-gradient-to-br from-[#5B9BD5] to-[#4A8BC2] text-white shadow-lg"
                style={{
                  gap: "0px",
                }}
              >
                <CardHeader className="pb-3">
                  <CardTitle className="text-white">Receipt Summary</CardTitle>
                </CardHeader>

                <CardContent className="p-4 sm:p-6 space-y-3">
                  {summaryItems.map(({ label, cents }) => (
                    <div
                      key={label}
                      className="flex flex-wrap justify-between items-center gap-2 pb-3 border-b border-white/20"
                    >
                      <span className="text-white/80">{label}</span>

                      <span
                        aria-live="polite"
                        className="text-xl sm:text-2xl text-white text-right"
                      >
                        {(cents / 100).toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                  ))}

                  <p className="text-xs text-white/70">
                    Total amount Received for this booking receipt.
                  </p>
                </CardContent>
              </Card>

              {/* Save draft */}
              {!editingId && (
                <Button
                  type="button"
                  onClick={() => handleSubmit("DRAFT")}
                  disabled={
                    loading ||
                    lookupsLoading ||
                    !!lookupError ||
                    lawazimLoading ||
                    !!lawazimError ||
                    !mayCreate
                  }
                  className="w-full text-white shadow-lg hover:opacity-90 h-12"
                  style={{
                    backgroundColor: "#5B9BD5",
                  }}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save As Draft
                </Button>
              )}

              {/* Save receipt */}
              <Button
                type="button"
                onClick={() => handleSubmit(editingId ? "UPDATE" : "POSTED")}
                disabled={
                  loading ||
                  lookupsLoading ||
                  !!lookupError ||
                  lawazimLoading ||
                  !!lawazimError ||
                  !mayCreate
                }
                className="w-full text-white shadow-lg hover:opacity-90 h-12"
                style={{
                  backgroundColor: "#5B9BD5",
                }}
              >
                <Save className="w-4 h-4 mr-2" />
                {editingId ? "Update Receipt" : "Post Receipt"}
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={() => onNavigate(listPath)}
                className="w-full border-gray-300"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </form>
    </>
  );
}
